import type { AlbumBackground, AlbumPicture } from '../../types';
import type { AlbumLook } from '../../utils/albumLook';
import { tripPhotoUrl } from '../../utils/tripPhotos';

interface Props {
  look: AlbumLook;
  photo: AlbumPicture | undefined;
  /** Page size in mm: patterns are drawn in mm so the PDF tiles them the same. */
  width: number;
  height: number;
}

const VEIL = { dark: 'rgba(15, 23, 42, 0.45)', light: 'rgba(255, 255, 255, 0.6)' } as const;

/** ADR-0048: the page's paper — a colour, a vector pattern on it, or a photo under a veil. */
export default function AlbumPageBackground({ look, photo, width, height }: Props) {
  if (look.photo && photo) {
    return (
      <>
        <img src={tripPhotoUrl(photo.thumbnailFilename ?? photo.filename)} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: VEIL[look.photo.veil] }} />
      </>
    );
  }
  return (
    <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden>
      <rect width={width} height={height} fill={look.background.color} />
      {look.background.pattern && <Pattern id={look.background.id} pattern={look.background.pattern} width={width} height={height} />}
    </svg>
  );
}

export function Pattern({ id, pattern, width, height }: {
  id: string; pattern: NonNullable<AlbumBackground['pattern']>; width: number; height: number;
}) {
  const { kind, color, spacing: d, size } = pattern;
  const patternId = `album-bg-${id}`;
  return (
    <>
      <defs>
        <pattern id={patternId} width={d} height={d} patternUnits="userSpaceOnUse"
          patternTransform={kind === 'stripes' ? 'rotate(45)' : undefined}>
          {kind === 'dots' && <circle cx={d / 2} cy={d / 2} r={size} fill={color} />}
          {kind === 'stripes' && <rect width={size} height={d} fill={color} />}
          {kind === 'grid' && <path d={`M${d},0L0,0L0,${d}`} fill="none" stroke={color} strokeWidth={size} />}
          {kind === 'notebook' && <line x1={0} y1={d} x2={d} y2={d} stroke={color} strokeWidth={size} />}
        </pattern>
      </defs>
      <rect width={width} height={height} fill={`url(#${patternId})`} />
    </>
  );
}
